<template>
  <DsPageContent>
    <DsHeroWithMedia
      v-if="content"
      :screen="screen"
      :eyebrow="content.deviceType?.name || ''"
      :image="content.primaryImage"
      :description="content.terminalDescription"
      :title="content.terminalName"
      :links="[content.salesLink]"
      class="ds-margin-bottom-48 ds-md-margin-bottom-96"
    />

    <!--  Benefits Section    -->
    <DsSection
      v-if="content.benefitsCollection"
      :with-space-top="false"
      with-space-bottom
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.benefitsCollection"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            class="ds-color-black"
            :icon="item.icon"
            :title="item.title"
            alignment="left"
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSection>

    <!--  Video player   -->
    <DsContainedSection
      v-if="content.introMedia"
      :with-space="false"
      :class="!!content.logoBar ? '' : 'ds-padding-bottom-96'"
    >
      <div class="ds-grid">
        <div
          v-if="content.introMediaTitle"
          class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8 ds-padding-bottom-48"
        >
          <h2
            v-if="content.introMediaTitle"
            class="ds-heading"
            :class="{ 'ds-padding-bottom-12': content.introMediaDescription }"
          >
            {{ content.introMediaTitle }}
          </h2>
          <p
            v-if="content.introMediaDescription"
            class="ds-text"
          >
            {{ content.introMediaDescription }}
          </p>
        </div>
        <div class="ds-grid__col ds-grid__col--12">
          <DsVideoElement
            v-if="content.introMediaAutoPlay"
            v-bind="content.introMedia"
            :screen="screen"
          />
          <DsVideoPlayer
            v-else
            :aria-label-play="labels.play"
            :aria-label-close="labels.close"
            v-bind="content.introMedia"
            :screen="screen"
          />
        </div>
      </div>
    </DsContainedSection>

    <!--  Logobar Section    -->
    <DsContainedSection
      v-if="content.logoBar && content.logoBar.items.length <= 6"
      :with-space="false"
      class="ds-padding-y-48"
      :class="!!comparisonTabs ? 'ds-md-padding-bottom-96' : ''"
    >
      <div class="ds-grid">
        <div class="ds-grid__col ds-grid__col--12">
          <DsLogoBar
            v-if="content.logoBar"
            :items="content.logoBar.items"
            align-center
          />
        </div>
      </div>
    </DsContainedSection>
    <DsLogoBarDynamic
      v-else-if="content.logoBar && content.logoBar.items.length > 6"
      :items="content.logoBar.items"
      align-center
    />

    <!-- Quote Section -->
    <DsQuote
      v-if="content.quote"
      :contained="false"
      :with-space-top="false"
      :logo="content.quote.logo"
      :text="content.quote.quote"
      :author="content.quote.author"
      :extra-info="content.quote.authorDetails"
    />

    <!--  Display Comparison  -->
    <DsContainedSection
      v-if="comparisonTabs && comparisonTabs.items && comparisonTabs.items.length"
      id="comparison-tabs"
      :theme="tabsTheme"
      class="comparison-tabs comparison-tabs--fade-in"
      with-space
    >
      <div
        id="comparison-tabs-content"
        class="ds-grid"
      >
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8">
          <h2
            v-if="comparisonTabs.title"
            class="ds-heading"
          >
            {{ comparisonTabs.title }}
          </h2>
          <p
            v-if="comparisonTabs.description"
            class="ds-margin-top-12"
          >
            {{ comparisonTabs.description }}
          </p>
        </div>
      </div>
      <DsTabs
        id="comparison-tabs-tab-component"
        :label="comparisonTabs.title"
        :theme="tabsTheme"
        :screen="screen"
        use-grid
        use-grid-content
        use-grid-divider
        class="tabs ds-padding-top-24 ds-md-padding-top-48"
        @item-clicked="handleTabItemClicked"
      >
        <DsTabItem
          v-for="(tab, index) in comparisonTabs.items[0].items"
          :key="`tab-${index}`"
          :title="tab.title"
          class="tab-item"
        >
          <DsImageElement
            :key="`tab-image-${tab.title}`"
            class="ds-padding-top-48"
            :image="tab.image"
            :screen="screen"
          />
        </DsTabItem>
      </DsTabs>
    </DsContainedSection>

    <!-- Cards -->
    <DsContainedSection
      v-if="content.contentBlock1"
      :with-space="false"
      class="ds-padding-y-48"
      :class="[
        !!comparisonTabs ? 'ds-md-padding-top-96' : '',
        !!content.awardImage1 || !!content.useCases ? '' : 'ds-md-padding-bottom-96'
      ]"
      theme="grey"
    >
      <DsMediaTextBlock
        v-for="(item, index) in content.contentBlock1.items"
        :key="item.title"
        :orientation="index % 2 === 1 ? 'left' : 'right'"
        :eyebrow="item.eyebrow"
        :title="item.title"
        :image="item.image"
        :video="item.video"
        :screen="screen"
        :class="index !== 0 && 'ds-padding-top-48 ds-md-padding-top-96'"
        video-autoplay
      >
        <RichText :data="item.description" />
      </DsMediaTextBlock>
      <DsDivider
        v-if="content.awardImage1 || (content.useCases && content.useCases.title)"
        theme="grey"
        class="ds-margin-y-48"
      />
    </DsContainedSection>

    <!--  Awards Section  -->
    <DsContainedSection
      v-if="content.awardImage1"
      :with-space="false"
      class="ds-padding-y-48"
      :class="[!!content.useCases ? '' : 'ds-md-padding-bottom-96']"
      theme="grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <TerminalsAwardsBar
            theme="light"
            :primary-image="content.awardImage1"
            :secondary-image="content.awardImage2"
          />
        </div>
      </div>
    </DsContainedSection>

    <!--  Use Cases  -->
    <DsContainedSection
      v-if="content.useCases && content.useCases.items && content.useCases.items.length"
      full-screen
      theme="grey"
      :with-space="false"
      class="image-gallery-contained ds-padding-y-48 ds-md-padding-bottom-96"
    >
      <DsImageGallery
        :screen="screen"
        :items="content.useCases.items.length"
        :title="content.useCases.title"
        :description="content.useCases.description"
        :width="{ small: 272, large: 696 }"
      >
        <template #default="{ index }">
          <DsCard
            :key="`useCases-image-gallery-item-${index}`"
            :image="content.useCases.items[index - 1].image"
            :title="content.useCases.items[index - 1].title"
            :text="content.useCases.items[index - 1].description"
            :link="content.useCases.items[index - 1].link"
            aspect-ratio="4/3"
            with-content-padding
            class="ds-background-color-white ds-height-full"
          />
        </template>
      </DsImageGallery>
    </DsContainedSection>

    <!-- Features Section -->
    <DsSectionGroup
      v-if="content.specsTitle"
      :title="content.specsTitle"
      :with-space="false"
      class="ds-padding-y-48 ds-md-padding-top-96"
      :class="!!content.footnotes ? '' : 'ds-md-padding-bottom-96'"
      theme="light"
    >
      <div class="ds-grid">
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6">
          <DsImageElement
            :image="content.secondaryImage"
            :screen="screen"
          />
        </div>
        <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6">
          <DsAccordion>
            <DsAccordionItem
              v-for="(section, sectionIndex) in sectionLabels"
              :key="section.value"
              :title="section.label"
              class="accordion-wrapper"
              :opened="sectionIndex === 0"
            >
              <DsDivider />
              <div
                v-for="(row, index) in tableRows[section.value]"
                :key="index"
                class="accordion-row"
              >
                <div class="ds-font-weight-semi-bold">
                  {{ row.label }}
                </div>
                <div
                  v-if="row.value === true"
                  class="ds-text-align-right"
                >
                  <DsIconCore
                    class="ds-color-green"
                    category="interface"
                    name="checkmark-circle-fill"
                  />
                </div>
                <div
                  v-else-if="row.value === '-' || row.value === false"
                  class="ds-text-align-right"
                >
                  <DsIconCore
                    class="ds-color-grey"
                    category="interface"
                    name="cross-circle-fill"
                  />
                </div>
                <div
                  v-else
                  class="ds-text-align-right"
                >
                  {{ row.value }}
                </div>
              </div>
            </DsAccordionItem>
            <DsAccordionItem
              v-if="countriesPerRegion && countriesPerRegion.length > 0"
              :title="terminalsLabels.geographicalAvailability"
              class="accordion-wrapper"
            >
              <DsDivider />
              <p
                v-for="(row, index) in countriesPerRegion"
                :key="index"
                class="accordion-row__geo"
              >
                <span class="ds-font-weight-semi-bold">
                  {{ row.region }}
                </span>
                <span class="accordion-row__countries">
                  {{ row.countries }}
                </span>
              </p>
            </DsAccordionItem>
          </DsAccordion>
        </div>
      </div>
    </DsSectionGroup>

    <!--  Accessories  -->
    <DsSection
      v-if="content.accessories && content.accessories.items && content.accessories.items.length"
      full-screen
      :with-space="false"
      class="ds-padding-y-48"
      :class="!!content.formId || !!content.footnotes ? '' : 'ds-md-padding-bottom-96'"
    >
      <DsImageGallery
        :screen="screen"
        :items="content.accessories.items.length"
        :title="content.accessories.title"
        :description="content.accessories.description"
        :width="{ small: 272, large: 456 }"
      >
        <template #default="{ index }">
          <DsCard
            :key="`accessory-gallery-item-${index}`"
            :image="content.accessories.items[index - 1].image"
            :title="content.accessories.items[index - 1].title"
            :text="content.accessories.items[index - 1].description"
            aspect-ratio="4/3"
            class="ds-background-color-white ds-height-full"
          />
        </template>
      </DsImageGallery>
    </DsSection>

    <!--  Form Section   -->
    <DsSection
      v-if="content.formId"
      :with-space="false"
      class="ds-padding-y-48"
      :class="!!content.footnotes ? '' : 'ds-md-padding-bottom-96'"
      theme="light"
    >
      <div class="ds-grid ds-md-justify-content-center">
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--6">
          <ContactForm
            :form-id="content.formId"
            :title="content.formTitle"
            :description="content.formDescription"
            center-title
          />
        </div>
      </div>
    </DsSection>

    <!--  Footnotes  -->
    <DsSection
      v-if="content.footnotes"
      :with-space="false"
      class="ds-padding-y-48 ds-md-padding-bottom-96"
      theme="light"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <RichText
            class="ds-text-small footnotes"
            :data="content.footnotes"
          />
        </div>
      </div>
    </DsSection>

    <!-- Call to action -->
    <DsCallToAction
      v-if="content.cta"
      :title="content.cta.title"
      :text="content.cta.text"
      :primary-link="content.cta.primaryLink"
    />
  </DsPageContent>
</template>

<script setup>
import {
  ModuleTerminalConnectivitySpecs,
  ModuleTerminalHardwareSpecs,
  ModuleTerminalIntegrationSpecs,
  ModuleTerminalPaymentExperienceSpecs,
  PageTerminalsDetail
} from '#layers/core/data-layer/definitions/terminals'
import {
  DsAccordion,
  DsAccordionItem,
  DsCallToAction,
  DsCard,
  DsCardTextIcon,
  DsContainedSection,
  DsDivider,
  DsGrid,
  DsHeroWithMedia,
  DsIconCore,
  DsImageElement,
  DsImageGallery,
  DsLogoBar,
  DsLogoBarDynamic,
  DsMediaTextBlock,
  DsPageContent,
  DsQuote,
  DsSection,
  DsSectionGroup,
  DsTabItem,
  DsTabs,
  DsVideoElement,
  DsVideoPlayer
} from 'awds'
import { getSectionsWithVariants, mapCountriesPerRegion } from '../../utils/device-utils'

definePageMeta({
  pageTransition: { name: 'page-fade', mode: 'out-in' }
})

const { screen } = useScreen()
const { locale } = useI18n()
const { labels, localeGlobalContentData } = useMainGlobalData()
const route = useRoute()
const { getSlug } = usePageUtils()
const slug = getSlug(route.path)

const { useStandardAsyncData, onAfterAdyenData, onBeforeAdyenData, content } = useMainPageData()

await onBeforeAdyenData({ globalDataContentType: PageTerminalsDetail.contentType })

const { data } = await useAsyncData('devices-detail-' + locale.value + '-' + slug, async () => {
  const page = await useStandardAsyncData(PageTerminalsDetail, { log: false })

  if (!page || !Object.keys(page).length) {
    return null
  }

  return page
})

await onAfterAdyenData(data.value)

const tabsTheme = ref('dark')

const terminalsLabels = computed(() => localeGlobalContentData?.value?.terminalsLabels || {})
const specLabels = computed(() => localeGlobalContentData?.value?.terminalsSpecsLabels || {})

const comparisonTabs = computed(() => {
  return content.value?.displayComparisonTabs
})

const sectionsWithVariants = computed(() => {
  return getSectionsWithVariants(content.value.specs)
})

const sectionLabels = computed(() => {
  return sectionsWithVariants.value.map((sectionWithVariant) => {
    const [section, variant] = sectionWithVariant.split('==')
    const label = `${terminalsLabels.value[section]}${variant ? ` - ${variant}` : ''}`
    return {
      label,
      value: sectionWithVariant
    }
  })
})

const tableRows = computed(() => {
  return (
    Object.fromEntries(
      sectionsWithVariants.value.map((section) => {
        return [section, mapSpecsToTable(section)]
      })
    ) || {}
  )
})

const specsFields = computed(() => {
  return {
    hardware: ModuleTerminalHardwareSpecs.fields.map((f) => f.id),
    connectivity: ModuleTerminalConnectivitySpecs.fields.map((f) => f.id),
    paymentExperience: ModuleTerminalPaymentExperienceSpecs.fields.map((f) => f.id),
    integration: ModuleTerminalIntegrationSpecs.fields.map((f) => f.id)
  }
})

const countriesPerRegion = computed(() => {
  return mapCountriesPerRegion(
    localeGlobalContentData.value?.globalDataRegions || [],
    content.value.countryAvailabilityCollection
  )
})

const mapSpecsToTable = (sectionWithVariant) => {
  const [section, variant] = sectionWithVariant.split('==')
  const hasVariant = !!variant

  return specsFields.value[section].map((specKey) => {
    const hasSpecs = content.value.specs[section]?.length > 0
    let specs

    if (!hasSpecs) {
      specs = {}
    } else if (hasVariant) {
      // With specs and YES variant
      specs = content.value.specs[section].find((_specs) => _specs.deviceVariant === variant) || {}
    } else {
      specs = content.value.specs[section][0] || {}
    }

    // Special case for Ethernet field in Connectivity section since it can have 3 states
    // Ticket WEB-4676
    if (section === 'connectivity' && specKey === 'ethernet') {
      let value = specs.ethernet ? specLabels.value.ethernetValueYes : specLabels.value.ethernetValueNo
      if (specs.ethernetWithBaseStation) {
        value = specLabels.value.ethernetValueYesWithBaseStation
      }

      return {
        label: specLabels.value.ethernet,
        value
      }
    }

    return {
      label: specLabels.value[specKey],
      value: specs[specKey] || '-'
    }
  })
}

// Comparison Tabs click handler
const handleTabItemClicked = () => {
  // update theme
  tabsTheme.value = tabsTheme.value === 'light' ? 'dark' : 'light'

  const element = document.getElementById('comparison-tabs')
  const content = document.getElementById('comparison-tabs-content')
  const tabs = document.getElementById('comparison-tabs-tab-component')
  // reset animation
  element.classList.remove('comparison-tabs--fade-in')
  content.classList.remove('comparison-tabs__content--fade-in')
  tabs.classList.remove('comparison-tabs__content--fade-in')
  // eslint-disable-next-line
  void element.offsetWidth
  // start animation
  element.classList.add('comparison-tabs--fade-in')
  content.classList.add('comparison-tabs__content--fade-in')
  tabs.classList.add('comparison-tabs__content--fade-in')
}
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.accordion-row {
  display: flex;
  margin-top: $spacing-24;

  > div {
    flex: 1;
  }

  &__geo {
    margin-top: $spacing-24;

    @include screen('large') {
      display: flex;
    }

    > span {
      display: block;

      @include screen('large') {
        flex: 1;
      }
    }
  }

  &__countries {
    margin-top: $spacing-8;

    @include screen('large') {
      margin-top: 0;
      text-align: right;
    }
  }
}

.use-cases-item {
  display: flex;
  flex-direction: column;
  min-height: 52px;

  p {
    flex-grow: 1;
  }
}

.comparison-tabs {
  transition: background-color 0.1s cubic-bezier(0.55, 0.055, 0.675, 0.19);

  :deep(.ds-dropdown__button) {
    @include screen('small') {
      padding-left: 0 !important;
    }
  }

  :deep(.ds-contained-section--dark),
  :deep(.ds-contained-section--light) {
    transition: background-color 0.1s cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }

  &__content--fade-in {
    -webkit-animation: fade-in 0.1s cubic-bezier(0.55, 0.055, 0.675, 0.19) both;
    animation: fade-in 0.1s cubic-bezier(0.55, 0.055, 0.675, 0.19) both;
    transition: background-color 0.1s cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}

// @TODO: Fix for image gallery when is renderd inside a contained section
.image-gallery-contained {
  overflow: hidden;

  :deep(.ds-container.image-gallery__container) {
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
